import React, { useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { BookMarked, Plus } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API } from '../governance_v2/constants';
import { EVENT_LABELS, STATUS_META, VERDICT_META } from './constants';
import { MemoryDetailDialog } from './MemoryDetailDialog';
import { NewMemoryDialog } from './NewMemoryDialog';
import '../materiality/materiality.css';

const PAGE = 20;

const Filter = ({ value, onChange, items, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-48" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const MemoryCard = ({ m, index, onOpen }) => (
  <button type="button" onClick={() => onOpen(m.id)} data-testid={`memory-card-${m.id}`} style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
    className="mat-reveal block w-full rounded-2xl border border-slate-200 bg-white p-4 text-left transition-shadow duration-200 hover:shadow-lg dark:border-slate-700 dark:bg-slate-900">
    <div className="flex flex-wrap items-center gap-2">
      <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${VERDICT_META[m.outcome.verdict]?.cls}`}>{VERDICT_META[m.outcome.verdict]?.label}</span>
      <span className="text-xs text-slate-500">{EVENT_LABELS[m.event]}</span>
      {m.outcome_changed && <Badge className="on-solid bg-orange-600 text-[10px] text-white">Resultado cambió</Badge>}
      <Badge variant="outline" className={`ml-auto text-[10px] ${STATUS_META[m.status].cls}`} data-testid={`memory-status-${m.id}`}>{STATUS_META[m.status].label}</Badge>
    </div>
    <p className="mt-2 font-semibold text-slate-900 dark:text-white">{m.title}</p>
    <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-300">{m.lesson}</p>
    <p className="mt-2 text-[11px] text-slate-400">{new Date(m.created_at).toLocaleDateString('es')}{m.context?.module_type ? ` · ${m.context.module_type}` : ''}{m.context?.kpi_names?.length ? ` · ${m.context.kpi_names.join(', ')}` : ''}</p>
  </button>
);

export const MemoryPanel = ({ companyId, role }) => {
  const isAdmin = role !== 'staff';
  const [status, setStatus] = useState('all');
  const [verdict, setVerdict] = useState('all');
  const [event, setEvent] = useState('all');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [openId, setOpenId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const params = { ...(status !== 'all' && { status }), ...(verdict !== 'all' && { verdict }), ...(event !== 'all' && { event }), ...(q.length >= 3 && { q }) };
  const { data, isLoading } = useQuery({ queryKey: ['memory', 'list', companyId, params], enabled: !!companyId,
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/memory`, { params })).data });
  const items = data?.items || [];
  const reset = (fn) => (v) => { fn(v); setLimit(PAGE); };
  return (
    <div className="space-y-6" data-testid="memory-panel">
      <div className="flex flex-wrap items-center gap-3">
        <BookMarked className="h-7 w-7 text-indigo-500" />
        <div className="mr-auto"><h2 className="text-2xl font-bold text-slate-900 dark:text-white">Memoria Institucional</h2>
          <p className="text-sm text-slate-500">Cada decisión cerrada deja su resultado real y una lección. Las aprobadas las usan SUPER-100, el Briefing del CEO y las nuevas propuestas.</p></div>
        {isAdmin && <Button onClick={() => setNewOpen(true)} data-testid="memory-new-btn"><Plus className="mr-2 h-4 w-4" />Nueva lección</Button>}
      </div>
      {isAdmin && data?.counts && <div className="flex flex-wrap gap-3" data-testid="memory-counts">
        {Object.entries(STATUS_META).map(([k, v]) => <div key={k} className="rounded-2xl border border-slate-200 px-4 py-2 dark:border-slate-700" data-testid={`memory-count-${k}`}>
          <p className="text-[11px] uppercase text-slate-500">{v.plural}</p><p className="text-xl font-bold tabular-nums">{data.counts[k]}</p></div>)}
      </div>}
      <div className="flex flex-wrap gap-2">
        {isAdmin && <Filter value={status} onChange={reset(setStatus)} testid="memory-filter-status" items={[['all', 'Todos los estados'], ...Object.entries(STATUS_META).map(([k, v]) => [k, v.label])]} />}
        <Filter value={verdict} onChange={reset(setVerdict)} testid="memory-filter-verdict" items={[['all', 'Todos los resultados'], ...Object.entries(VERDICT_META).map(([k, v]) => [k, v.label])]} />
        <Filter value={event} onChange={reset(setEvent)} testid="memory-filter-event" items={[['all', 'Todos los orígenes'], ...Object.entries(EVENT_LABELS)]} />
        <Input className="w-64" placeholder="Buscar (mín. 3 letras)…" value={q} onChange={(e) => reset(setQ)(e.target.value)} data-testid="memory-search" />
      </div>
      {isLoading ? <p className="text-sm text-slate-400">Cargando memoria…</p> : items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="memory-empty">
          {isAdmin ? 'Aún no hay memorias. Se crean solas al cerrar proyectos, medir predicciones, rechazar o dejar vencer propuestas y resolver alertas con propuesta.' : 'Aún no hay lecciones aprobadas.'}</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2" data-testid="memory-list">
          {items.slice(0, limit).map((m, i) => <MemoryCard key={m.id} m={m} index={i} onOpen={setOpenId} />)}
        </div>
      )}
      {items.length > limit && <Button variant="ghost" className="w-full" onClick={() => setLimit(limit + PAGE)} data-testid="memory-load-more">Mostrar más ({items.length - limit} restantes)</Button>}
      <MemoryDetailDialog companyId={companyId} memoryId={openId} isAdmin={isAdmin} onClose={() => setOpenId(null)} />
      {isAdmin && <NewMemoryDialog companyId={companyId} open={newOpen} onClose={() => setNewOpen(false)} />}
    </div>
  );
};
